// Control-plane client (D-056). Lets Connections pair the user's trading
// server (the agent that dials out to our backend) and read its live status.
// This milestone is pair + status only; secrets and strategy/command
// deployment land in later milestones. See 02_ENGINEERING/Pi_Control_Plane.md.

import { storageGet } from "./storage.js";
import { getStoredLicense } from "./license.js";
import { seal } from "./sealbox.js";
import { deployEnvelope, type Strategy } from "./composable/model.js";
import { econCalendarFile } from "./econ-calendar.js";
import { newsFeedFile } from "./news-feed.js";
import { earningsFeedFile } from "./earnings-feed.js";

const DEFAULT_API_BASE = "https://api.tickahead.com";

async function apiBase(): Promise<string> {
  return await storageGet<string>("licenseApiBase", DEFAULT_API_BASE);
}

async function authHeader(): Promise<Record<string, string> | null> {
  const license = await getStoredLicense();
  if (!license?.token) return null;
  return { authorization: `Bearer ${license.token}` };
}

/** Fields the agent reports in its heartbeat (all optional — the agent sends
 * what it has; the UI shows what's present). */
export interface Position {
  symbol: string;
  side: "long" | "short";
  qty: number;
  entry: number;
  uPnL?: number; // unrealized P&L
}

/** What the engine writes to status.json (the agent forwards it verbatim).
 * Contract settled with Trading 2026-08-09. */
export interface ServerHeartbeat {
  strategy?: string; // deployed strategy's display name
  account?: string; // the account_id it's trading
  mode?: string; // "dry-run" | "live"
  state?: string; // "flat" | "in-position" | "stopped"
  openPositions?: number | Position[]; // count or details
  pnlToday?: number; // realized+unrealized, engine's unit
  ts?: number; // engine's own heartbeat (epoch ms)
  secrets_set?: string[]; // names of secrets the box holds (write-only feedback)
  [key: string]: unknown;
}

export interface ServerStatus {
  paired: boolean;
  name?: string;
  publicKey?: string;
  lastSeen?: number; // unix seconds
  status?: ServerHeartbeat | null;
}

/** null = not signed in / network error (caller keeps the prior view). */
export async function fetchServerStatus(): Promise<ServerStatus | null> {
  const headers = await authHeader();
  if (!headers) return null;
  try {
    const res = await fetch(`${await apiBase()}/v1/server/status`, { headers });
    if (!res.ok) return { paired: false };
    return (await res.json()) as ServerStatus;
  } catch {
    return null;
  }
}

export async function createPairingCode(): Promise<{ code: string; expiresAt: number } | { error: string }> {
  const headers = await authHeader();
  if (!headers) return { error: "Sign in first." };
  try {
    const res = await fetch(`${await apiBase()}/v1/server/pair-code`, { method: "POST", headers });
    const body = (await res.json().catch(() => ({}))) as { code?: string; expiresAt?: number; error?: string };
    if (!res.ok || !body.code) return { error: body.error ?? "Could not create a pairing code." };
    return { code: body.code, expiresAt: body.expiresAt ?? 0 };
  } catch {
    return { error: "Network error." };
  }
}

export async function unpairServer(): Promise<boolean> {
  const headers = await authHeader();
  if (!headers) return false;
  try {
    const res = await fetch(`${await apiBase()}/v1/server/unpair`, { method: "POST", headers });
    return res.ok;
  } catch {
    return false;
  }
}

/** Enqueue a command for the user's box (backend forwards to the agent). */
export async function sendCommand(type: string, payload?: unknown): Promise<boolean> {
  const headers = await authHeader();
  if (!headers) return false;
  try {
    const res = await fetch(`${await apiBase()}/v1/server/command`, {
      method: "POST",
      headers: { ...headers, "content-type": "application/json" },
      body: JSON.stringify({ type, payload }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Seal a secret to the box's public key in the browser, then enqueue it —
 * the backend only ever sees ciphertext. `publicKey` comes from the server
 * status (`publicKey`). */
export async function setSecret(name: string, publicKey: string, value: string): Promise<boolean> {
  const blob = await seal(publicKey, value);
  return sendCommand("set-secret", { name, blob });
}

/** Deploy a strategy to the box. Sealed like a secret — the strategy is the
 * trader's own IP, so the relay only ever sees ciphertext. Wrapped in the
 * `builder-graph` envelope (D-064): the builder's COMPILED primitive graph +
 * per-setup entry/exit node ids, which the on-box RuleStrategy VM runs directly
 * (backtest == live). The engine branches on `format` and reloads on `at`. */
export async function deployStrategy(publicKey: string, strategy: Strategy): Promise<boolean> {
  const envelope = deployEnvelope(strategy);
  const blob = await seal(publicKey, JSON.stringify(envelope));
  return sendCommand("deploy-strategy", { name: strategy.name, blob });
}

/** Push the curated econ-calendar to the box (D-065) so the `newsWindow` operand
 * has events to read. Global, not per-strategy; sent alongside a deploy and cheap
 * to resend. The agent writes it to `~/.tickahead/econ_calendar.json`. */
export async function deployCalendar(publicKey: string): Promise<boolean> {
  const blob = await seal(publicKey, JSON.stringify(econCalendarFile()));
  return sendCommand("set-calendar", { blob });
}

/** Push the news feed to the box (D-072) so the `newsEvent` operand has classified
 * events to read — the `newsEvent` twin of deployCalendar. Empty until the backend
 * news engine lands; the agent writes it to `~/.tickahead/news_feed.json`. */
export async function deployNews(publicKey: string): Promise<boolean> {
  const blob = await seal(publicKey, JSON.stringify(newsFeedFile()));
  return sendCommand("set-news", { blob });
}

/** Push the earnings-date feed to the box (D-091) so a scheduled-event rule with
 * type "Earnings" has dates to read — the earnings twin of deployCalendar. Empty
 * until an earnings source is wired; the agent writes `~/.tickahead/earnings.json`. */
export async function deployEarnings(publicKey: string): Promise<boolean> {
  const blob = await seal(publicKey, JSON.stringify(earningsFeedFile()));
  return sendCommand("set-earnings", { blob });
}

/** Control the box's engine: start / stop / flatten. */
export async function sendControl(action: "start" | "stop" | "flatten"): Promise<boolean> {
  return sendCommand("control", { action });
}

/** Switch the box between paper (dry-run) and live trading. */
export async function setMode(mode: "dry-run" | "live"): Promise<boolean> {
  return sendCommand("set-mode", { mode });
}

// --- DigitalOcean "Connect your cloud" (D-057). Retail-only path: the user
// connects their OWN DigitalOcean account so we can provision their droplet.
// Prop-firm users never see this — VPS is banned there.

export interface DoStatus {
  connected: boolean;
  connectedAt?: number | null; // unix seconds
  configured: boolean; // false = backend has no DO app credentials yet
}

/** Get the DigitalOcean authorize URL to open in a new tab. null = not signed
 * in; { error } = backend not configured yet. */
export async function doAuthorizeUrl(): Promise<{ url: string } | { error: string } | null> {
  const headers = await authHeader();
  if (!headers) return null;
  try {
    const res = await fetch(`${await apiBase()}/v1/do/authorize`, { headers });
    const body = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
    if (!res.ok || !body.url) {
      return { error: body.error === "do_not_configured" ? "Cloud connect isn't available yet." : body.error ?? "Could not start DigitalOcean connect." };
    }
    return { url: body.url };
  } catch {
    return { error: "Network error." };
  }
}

/** null = not signed in / network error (caller keeps prior view). */
export async function doStatus(): Promise<DoStatus | null> {
  const headers = await authHeader();
  if (!headers) return null;
  try {
    const res = await fetch(`${await apiBase()}/v1/do/status`, { headers });
    if (!res.ok) return { connected: false, configured: false };
    return (await res.json()) as DoStatus;
  } catch {
    return null;
  }
}

export async function doDisconnect(): Promise<boolean> {
  const headers = await authHeader();
  if (!headers) return false;
  try {
    const res = await fetch(`${await apiBase()}/v1/do/disconnect`, { method: "POST", headers });
    return res.ok;
  } catch {
    return false;
  }
}

export interface DoRegion {
  slug: string;
  name: string;
}

/** Regions on the user's DO account where the smallest droplet is available.
 * null = not signed in / network error (caller falls back to no picker). */
export async function doRegions(): Promise<DoRegion[] | null> {
  const headers = await authHeader();
  if (!headers) return null;
  try {
    const res = await fetch(`${await apiBase()}/v1/do/regions`, { headers });
    if (!res.ok) return null;
    const body = (await res.json().catch(() => ({}))) as { regions?: DoRegion[] };
    return body.regions ?? [];
  } catch {
    return null;
  }
}

/** Spin up a droplet on the user's connected DigitalOcean account, provisioned
 * to auto-pair. On success the box appears via the normal server status poll.
 * `region` is a DO slug (e.g. "lon1"); omit to let the backend default. null =
 * not signed in. */
export async function doProvision(region?: string): Promise<{ ok: true } | { error: string; reconnect?: boolean } | null> {
  const headers = await authHeader();
  if (!headers) return null;
  try {
    const res = await fetch(`${await apiBase()}/v1/do/provision`, {
      method: "POST",
      headers: { ...headers, "content-type": "application/json" },
      body: JSON.stringify(region ? { region } : {}),
    });
    const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; detail?: string };
    if (res.ok && body.ok) return { ok: true };
    if (body.error === "digitalocean_auth_expired") return { error: "Your DigitalOcean connection expired — reconnect and try again.", reconnect: true };
    if (body.error === "digitalocean_not_connected") return { error: "Connect DigitalOcean first.", reconnect: true };
    return { error: body.detail ?? "Could not create the server. Try again in a moment." };
  } catch {
    return { error: "Network error." };
  }
}

/** The agent heartbeats often; treat a gap over ~90s as offline. */
export function isOnline(lastSeen: number | undefined, now = Date.now()): boolean {
  return lastSeen != null && now / 1000 - lastSeen < 90;
}

/** "just now" / "3m ago" / "2h ago" from a unix-seconds timestamp. */
export function agoLabel(lastSeen: number | undefined, now = Date.now()): string {
  if (lastSeen == null) return "never";
  const secs = Math.max(0, Math.floor(now / 1000 - lastSeen));
  if (secs < 45) return "just now";
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}
